import { Button, Card, CircularProgress, Typography } from "@mui/material";
import { FastField, Form, Formik, Field } from "formik";
import { TextField, Select, CheckboxWithLabel } from "formik-mui";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { businessGet, businessEdit, businessDelete } from "../api/api";
import Box from "@mui/material/Box";
import { useCallback, useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import * as Yup from "yup";
import MuiPhoneNumber from "material-ui-phone-number-2";
import phone from "phone";
import { useProfile } from "../api/hooks";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useMedia } from "react-use";
import ct from "countries-and-timezones";
import { useSnackbar } from "../store/store";

export const BusinessEdit = () => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const isDarkTheme = useMedia("(prefers-color-scheme: dark)");
  const backUrl = "/";
  const countries = ct.getAllCountries();
  const snackbar = useSnackbar();

  const alert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const hasPathParam = !!businessId;

  const isSuperAdmin =
    profile?.roles?.some((role) => role.type === "SUPER_ADMIN") ?? false;
  const isAdmin =
    (profile?.roles?.some((role) => role.type === "ADMIN") || isSuperAdmin) ??
    false;

  const isWide = useMedia("(min-width: 600px)");

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery(["business", businessId], businessGet(businessId!), {
    enabled: hasPathParam,
    refetchOnWindowFocus: false,
  });
  const business: any = response?.data;

  console.log("business", business);

  const {
    mutateAsync,
    error: mutateError,
    data: mutateData,
    isLoading: mutateIsLoading,
  } = useMutation("businessEdit", businessEdit, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        "business",
        "getUserAdminOfBusinesses",
        "profile",
      ]);
      navigate(backUrl);
    },
    onError: (err: any) => {
      alert("Error", err.message);
    },
  });

  const handlePhoneChange = useCallback(
    (originalHandler) => (value) => {
      originalHandler({ target: { name: "phone", value } });
    },
    []
  );

  return (
    <div className="flex flex-col gap-2 justify-center items-center w-full">
      <Dialog
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{`${alertTitle}`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`${alertMessage}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      {isLoading && (
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {(!hasPathParam || business) && (
        <>
          <Dialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Deletion confirmation"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {`This will delete the business with all of its events, event types, venues, venue parts, ticket sets and ticket types. The oranization will not be deleted if it has a future ending event. Are you sure you want to delete all of this?`}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  try {
                    const response = await businessDelete(business.id)();
                    setConfirmOpen(false);
                    if (response) navigate(backUrl);
                  } catch (error: any) {
                    setConfirmOpen(false);
                    alert(error.error, error.message);
                  }
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Formik
            validateOnMount
            initialValues={
              business || {
                name: "",
                legalName: "",
                email: "",
                phone: "",
                country: "",
                state: "",
                city: "",
                streetAddress: "",
                postalCode: "",
                taxId: "",
                taxBranchOfficeId: "",
                taxSerialNumber: "",
                type: "",
                enabled: false,
                primaryColor: "#4285F4",
                secondaryColor: "#4285F4",
              }
            }
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(2, "Too short")
                .max(50, "Too long")
                .required("Required"),

              email: Yup.string().email("Invalid email").required("Required"),
              phone: Yup.string()
                .test("phone", "Invalid phone number", (value) => {
                  console.log("phone", phone(value || "").isValid);

                  if (!value) return true;
                  return phone(value).isValid;
                })
                .required("Required"),
              country: Yup.string().required("Required"),
            })}
            onSubmit={async ({ ...values }, { setSubmitting }) => {
              console.log(values);

              try {
                const response = await mutateAsync({ values });
                if (
                  !hasPathParam &&
                  !values?.enabled &&
                  response?.status === 201
                )
                  snackbar.open({
                    type: "success",
                    message:
                      "Thank you for your interest. We will contact you.",
                  });
              } catch (error: any) {
                alert(error.error, error.message);
              }
              setSubmitting(false);
            }}
          >
            {({
              submitForm,
              isSubmitting,
              values,
              isValid,
              handleChange,
              errors,
              touched,
              handleBlur,
            }) => (
              <Form style={{ width: "100%" }}>
                <Card
                  variant="outlined"
                  sx={{
                    width: "100%",
                    maxWidth: "450px",
                    borderWidth: isDarkTheme ? "0px" : "1px",
                    padding: { xs: "16px 16px 24px", sm: "40px" },
                    margin: "16px auto 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      textAlign: "center",
                      mb: 2,
                      fontSize: "1.8rem",
                    }}
                  >
                    {hasPathParam ? "Edit business" : "Add your business"}
                  </Typography>
                  {!hasPathParam && (
                    <>
                      <Box sx={{ color: "red", textAlign: "center" }}>
                        The app is in early preview.
                        <br /> All data will be deleted.
                      </Box>
                      <Box sx={{ opacity: 0.7 }}>
                        We will contact you on the provided contact information.
                        You can start accepting payments and sending API
                        requests after we enable your business.
                      </Box>
                    </>
                  )}
                  <FastField
                    component={TextField}
                    label="Name"
                    name="name"
                    size="small"
                  />

                  <FastField
                    component={TextField}
                    label="Legal name"
                    name="legalName"
                    size="small"
                  />

                  <Box className="small-formik-select-fix">
                    <FastField
                      component={Select}
                      label="Country"
                      id="country"
                      name="country"
                      size="small"
                      required
                    >
                      {!!countries &&
                        Object.keys(countries).map((c) => (
                          <MenuItem
                            key={countries[c]?.id}
                            value={countries[c]?.name}
                          >
                            {countries[c]?.name}
                          </MenuItem>
                        ))}
                    </FastField>
                  </Box>
                  <FastField
                    component={TextField}
                    label="Contact email"
                    name="email"
                    type="email"
                    size="small"
                  />
                  <MuiPhoneNumber
                    required
                    defaultCountry={"rw"}
                    name="phone"
                    label="Contact phone"
                    variant="outlined"
                    value={values.phone}
                    onBlur={handleBlur}
                    onChange={handlePhoneChange(handleChange)}
                    error={touched.phone && !!errors.phone}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    helperText={(touched.phone && errors.phone) || ""}
                    className="MuiPhoneNumber"
                    InputLabelProps={{ required: false }}
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="State"
                    name="state"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="City"
                    name="city"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="Street address"
                    name="streetAddress"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="Postal code"
                    name="postalCode"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="Tax ID"
                    name="taxId"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="Tax Branch Office ID"
                    name="taxBranchOfficeId"
                    type="email"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="Tax Device Serial Number"
                    name="taxSerialNumber"
                    type="email"
                    size="small"
                  />
                  <FastField
                    component={TextField}
                    label="Primary color"
                    name="primaryColor"
                    size="small"
                    helperText="Hex color code (e.g. #ff0000)"
                  />
                  <FastField
                    component={TextField}
                    label="Secondary color"
                    name="secondaryColor"
                    size="small"
                    helperText="Hex color code (e.g. #ff0000)"
                  />
                  {isSuperAdmin && (
                    <Field
                      component={CheckboxWithLabel}
                      type="checkbox"
                      name="enabled"
                      Label={{ label: "Enabled" }}
                      size="small"
                    />
                  )}
                  <Box
                    sx={{
                      textAlign: "right",
                      margin: "8px 0 0",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      onClick={() => {
                        navigate(backUrl);
                      }}
                      sx={{ padding: "6px 8px" }}
                    >
                      Cancel
                    </Button>
                    <Box sx={{ flex: "1 1 auto" }} />
                    {hasPathParam && (
                      <Button
                        color="primary"
                        disabled={isSubmitting}
                        onClick={() => {
                          setConfirmOpen(true);
                        }}
                        sx={{ padding: "6px 8px" }}
                      >
                        Delete
                      </Button>
                    )}
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={isSubmitting || !isValid}
                      onClick={submitForm}
                      sx={{
                        padding: "6px 24px",
                        width: "138px",
                        height: "38px",
                      }}
                    >
                      {!mutateIsLoading ? (
                        "Add business"
                      ) : (
                        <CircularProgress size={"19px"} />
                      )}
                    </Button>
                  </Box>
                </Card>
              </Form>
            )}
          </Formik>
        </>
      )}
    </div>
  );
};

export default BusinessEdit;
